import ScrollableFlexGrow from "@src/lib/ui/ScrollableFlexGrow";
import useScrollTop from "@src/lib/hooks/useScrollTop";
import { FC, ReactNode, useMemo } from "react";
import React from "react";
import { useRouter } from "next/router";
import { UserType } from "@src/types.generated";
import useUser from "@src/features/app/hooks/useUser";
import NotFound from "@src/pages/404";
import useActionPermissions from "@src/features/app/hooks/useActionPermissions";
import { SidebarInset, SidebarProvider } from "@src/lib/ui/Sidebar";
import LeftNav from "@src/features/app/left-nav/LeftNav";
import { useIsTrainee } from "@src/features/app/hooks/useIsTrainee";
import LibraryItemOverviewOptIn from "@src/features/libraryItem/LibraryItemOverviewOptIn";
import PathsOptIn from "@src/features/workflows/PathsOptIn";
import TopNav from "@src/features/app/top-nav/TopNav";
import PageContentHeaderZone from "@src/features/app/top-nav/PageContentHeaderZone";
import PageContentHeaderZoneV2 from "@src/features/app/top-nav/PageContentHeaderZoneV2";
import { useIsRedesignedLibraryItemPage } from "@src/features/libraryItem/contexts/libraryItemMainTabStore";
import { isBuilderView } from "@src/features/app/top-nav/builderNav";
import { KnowledgeBaseChat } from "@src/features/assistantChat/KnowledgeBaseChat";
import { OpusAgentChat } from "@src/features/opus-agent/OpusAgentChat";
import {
  OpusAgentCanvasCovered,
  OpusAgentCanvasSlot,
} from "@src/features/opus-agent/OpusAgentCanvas";
import { useOpusAgentCanvasStore } from "@src/features/opus-agent/useOpusAgentCanvasStore";
import {
  isChatJoined,
  resolveAgentPanelGates,
  resolveChatSurface,
} from "@src/features/app/chatSurface";
import { AgentPanelGatesProvider } from "@src/features/opus-agent/agentPanel";
import { pageContentOverflowStyle } from "@src/features/app/pageContentOverflow";
import useIsFullAdmin from "@src/features/app/hooks/useIsFullAdmin";
import {
  useFeatureFlag,
  useFeatureFlagsLoaded,
} from "@src/features/app/hooks/useFeatureFlag";
import { useChatSidebar } from "@src/features/aiAssist/hooks/useChatSidebar";
import { AiChatType, Ff } from "@src/types.generated";
import { UserActionPermissionBooleans } from "@src/lib/contexts/ActionPermissionsContext";
import { cn } from "@src/lib/utils/cn";

export type ActionPermissionString = keyof UserActionPermissionBooleans;
type Props = {
  children: ReactNode;
  showTopNav?: boolean;
  // Full-screen views: the rail starts collapsed to icons on this page only.
  compactLeftNav?: boolean;
  defaultSidebarOpen?: boolean;
  overflow?: React.CSSProperties["overflow"];
  dataTestId?: string;
  forbiddenUserTypes: UserType[];
  requiredActionPermissions: ActionPermissionString[];
  hideKnowledgeBaseChat?: boolean;
  forceKnowledgeBaseChat?: boolean;
};

export const Page: FC<Props> = ({
  children,
  showTopNav = true,
  compactLeftNav = false,
  defaultSidebarOpen = true,
  overflow = "auto",
  dataTestId,
  forbiddenUserTypes,
  requiredActionPermissions,
  hideKnowledgeBaseChat = false,
  forceKnowledgeBaseChat = false,
}: Props) => {
  const { scrollProps } = useScrollTop();
  const { user } = useUser();
  const router = useRouter();
  const chatKnowledgeBaseEnabled = useFeatureFlag(Ff.ChatKnowledgeBase);
  const opusAgentEnabled = useFeatureFlag(Ff.OpusAgent);
  const opusAgentManagersEnabled = useFeatureFlag(Ff.OpusAgentManagers);
  const libraryItemOverviewTabEnabled = useFeatureFlag(
    Ff.LibraryItemOverviewTab,
  );
  // The zone follows the open page as well as the flag: the redesigned
  // pages report in through the store once their provider mounts.
  const redesignedLibraryItemPage = useIsRedesignedLibraryItemPage();
  const flagsResolved = useFeatureFlagsLoaded();
  const isFullAdmin = useIsFullAdmin();
  const { isTrainee } = useIsTrainee();
  const canvasOpen = useOpusAgentCanvasStore((s) => s.view !== null);
  const { isOpen: isKnowledgeBaseChatOpen } = useChatSidebar(
    AiChatType.KnowledgeBase,
  );

  const hideTopNavView =
    isBuilderView(router.pathname, router.query) ||
    router.pathname === "/path-builder";
  const showTopNavBar = showTopNav && !hideTopNavView;

  const LeftNavComponent = useMemo(
    () => <LeftNav compact={compactLeftNav} />,
    [compactLeftNav],
  );

  const { userActionPermissions, loading } = useActionPermissions();
  const hasPermission = useMemo(() => {
    if (loading || !user?.userType) {
      // avoid a 404 flash
      return true;
    }
    if (user?.userType && forbiddenUserTypes.includes(user.userType)) {
      return false;
    }
    if (userActionPermissions) {
      return requiredActionPermissions.every((ap) => userActionPermissions[ap]);
    }
    return false;
  }, [
    userActionPermissions,
    forbiddenUserTypes,
    requiredActionPermissions,
    loading,
    user?.userType,
  ]);

  if (!hasPermission) {
    return <NotFound />;
  }
  const shouldShowChat =
    showTopNav &&
    (chatKnowledgeBaseEnabled || forceKnowledgeBaseChat) &&
    !hideKnowledgeBaseChat;
  const chatSurface = resolveChatSurface({
    flagsResolved,
    // The same "user still loading" signal hasPermission uses above.
    userResolved: !!user?.userType,
    opusAgentEnabled,
    isFullAdmin,
    managerAgentsEnabled:
      user?.userType === UserType.Manager && opusAgentManagersEnabled,
  });
  const agentPanelGates = resolveAgentPanelGates({
    chatSurface,
    // The panel lives with the left nav, so full-screen views without the
    // top nav get it too. It does not depend on the KB opt-in.
    allowedOnPage: !hideKnowledgeBaseChat,
    isFullAdmin,
  });
  const chatJoined = isChatJoined({
    shouldShowChat,
    chatSurface,
    chatOpen: isKnowledgeBaseChatOpen,
  });

  return (
    <AgentPanelGatesProvider value={agentPanelGates}>
      <SidebarProvider
        defaultOpen={defaultSidebarOpen}
        className="h-[calc(100svh-var(--impersonation-banner-height))] max-h-[calc(100svh-var(--impersonation-banner-height))] min-h-0 w-screen min-w-0 bg-bg-canvas"
        style={
          {
            "--sidebar-width": "248px",
            "--admin-primary-rail-collapsed-width": "56px",
            "--admin-primary-rail-expanded-width": "200px",
            "--admin-secondary-menu-width": "220px",
            // The shell must never scroll sideways: the admin nav aside is
            // fixed, so a horizontal scroll here slides the in-flow page under
            // it and the aside eats clicks (OPUS-18385). Wide content scrolls
            // in its own container. The `overflow` prop covers the y axis only.
            // `hidden` still allows scripted scrolls (scrollIntoView, focus);
            // the wrapper below clips so there is nothing to scroll to.
            overflowY: overflow,
            overflowX: "hidden",
          } as React.CSSProperties
        }
        data-app-shell=""
      >
        {/* clip, not hidden: it is not a scroll container, so focus and
          scrollIntoView can't scroll it either. It must sit here, not on the
          shell: beside a scrolling y axis, clip computes to hidden. */}
        <div
          className="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-x-clip"
          data-testid={dataTestId}
        >
          <div className="flex min-h-0 min-w-0 flex-1 self-stretch">
            {LeftNavComponent}
            {agentPanelGates.askOpus && <OpusAgentChat />}
            <SidebarInset
              className={cn(
                "flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-xl bg-white shadow-default md:my-2 md:ml-0",
                agentPanelGates.askOpus &&
                  "md:min-w-[min(400px,18%)] lg:min-w-[min(400px,25%)] xl:min-w-[min(560px,36%)]",
                chatJoined ? "rounded-r-none" : "md:mr-2",
                // The admin rail shows at every width, so the page is a card
                // beside it below md too; the trainee nav stays a mobile sheet.
                // max-md:isolate keeps page chrome under the secondary menu,
                // which overlays the page below md.
                !isTrainee && "max-md:my-2 max-md:isolate",
                !isTrainee && !chatJoined && "max-md:mr-2",
                // While the agent canvas is open the card is its own stacking
                // context: the canvas clears in-card toolbars (z-[750]) while
                // body-portaled dialogs and popovers (z-50) still sit above it.
                canvasOpen && "isolate",
              )}
            >
              {/* Inert while the canvas covers it; the slot stays live. */}
              <OpusAgentCanvasCovered>
                {showTopNavBar ? <TopNav /> : null}
                {showTopNav ? (
                  <>
                    <div
                      id="top-nav-banner"
                      className="flex w-full min-w-0 shrink-0 flex-col empty:hidden"
                    />
                    {libraryItemOverviewTabEnabled ||
                    redesignedLibraryItemPage ? (
                      <PageContentHeaderZoneV2 />
                    ) : (
                      <PageContentHeaderZone />
                    )}
                    <div
                      id="top-nav-subheader"
                      className="flex min-h-10 w-full min-w-0 shrink-0 items-center justify-between gap-3 border-b border-border-primary px-3 empty:hidden md:px-4"
                    />
                    <div
                      id="top-nav-tertiary"
                      className="flex min-h-10 w-full min-w-0 shrink-0 items-center gap-2 px-2 empty:hidden md:px-4"
                    />
                  </>
                ) : null}
                <ScrollableFlexGrow
                  flex={1}
                  alignSelf={"stretch"}
                  innerStyle={{
                    alignItems: "center",
                  }}
                  onScroll={scrollProps.onScroll}
                >
                  <div
                    id={"page-content-inner"}
                    {...scrollProps}
                    className="flex flex-1 self-stretch"
                    style={pageContentOverflowStyle(overflow)}
                  >
                    {children}
                  </div>
                </ScrollableFlexGrow>
                {/* Pinned to the page's own bottom-left corner, so it sits just
                  past the nav or, when open, the agent panel, never over it. */}
                {!isTrainee && (
                  <div className="absolute bottom-1 left-2 z-40">
                    {/* Both render null off their own route, so only one can
                      ever occupy this slot. */}
                    <LibraryItemOverviewOptIn />
                    <PathsOptIn />
                  </div>
                )}
              </OpusAgentCanvasCovered>
              {/* The agent canvas covers this card (not the nav or the panel),
                  above the page, its corner opt-ins and builder toolbars, below
                  builder dialogs and menus. */}
              {agentPanelGates.askOpus && <OpusAgentCanvasSlot />}
            </SidebarInset>
            {shouldShowChat && chatSurface === "knowledge-base" && (
              <KnowledgeBaseChat />
            )}
          </div>
        </div>
      </SidebarProvider>
    </AgentPanelGatesProvider>
  );
};

export default Page;
